import { Box, Button, FileInput, Stack, Text, Textarea, TextInput } from "@mantine/core"
import { BarcodeIcon, DownloadSimpleIcon, FileXlsIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx"
import { message } from "../../../utils/message"
import { appendCode, parseLines, sheetToText } from "./parse"
import classes from "./quick-order.module.css"

export type QuickOrderMode = "sku" | "excel" | "barcode"

const MAX_FILE_BYTES = 5 * 1024 * 1024

type Props = {
  mode: QuickOrderMode
  text: string
  onTextChange: (text: string) => void
}

/** Left card of OrdB_QuickOrder: the source lines, typed, read from a file or scanned. */
export function QuickOrderInput({ mode, text, onTextChange }: Readonly<Props>) {
  const { t } = useTranslation("bulkOrder")

  const lines = (
    <Stack gap="xs">
      <Text className={classes.label}>{t("input.label")}</Text>
      <Textarea
        value={text}
        onChange={(e) => onTextChange(e.currentTarget.value)}
        autosize
        minRows={9}
        maxRows={18}
        spellCheck={false}
        aria-label={t("input.label")}
        classNames={{ input: classes.textarea }}
      />
      <Text className={classes.hint}>{t("input.hint")}</Text>
    </Stack>
  )

  return (
    <Box className={`${classes.card} ${classes.cardPad}`}>
      <Stack gap="md">
        {mode === "excel" && <ExcelSource onTextChange={onTextChange} />}
        {mode === "barcode" && <BarcodeSource text={text} onTextChange={onTextChange} />}
        {lines}
      </Stack>
    </Box>
  )
}

function ExcelSource({ onTextChange }: Readonly<{ onTextChange: (text: string) => void }>) {
  const { t } = useTranslation("bulkOrder")
  const [readCount, setReadCount] = React.useState<number | null>(null)

  const handleFile = async (file: File | null) => {
    setReadCount(null)
    if (!file) return
    if (file.size > MAX_FILE_BYTES) {
      message.error(t("excel.tooLarge"))
      return
    }
    try {
      const book = XLSX.read(await file.arrayBuffer(), { type: "array" })
      const sheet = book.Sheets[book.SheetNames[0]]
      const matrix = sheet ? XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false }) : []
      const next = sheetToText(matrix)
      onTextChange(next)
      setReadCount(parseLines(next).length)
    } catch {
      message.error(t("excel.readFailed"))
    }
  }

  const handleTemplate = () => {
    const sheet = XLSX.utils.aoa_to_sheet([["SKU", t("excel.qtyHeader")], ["TCTA31PB0025010", 10]])
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, "Sheet1")
    XLSX.writeFile(book, "quick-order.xlsx")
  }

  return (
    <Stack gap="xs">
      <FileInput
        label={t("excel.label")}
        placeholder={t("excel.placeholder")}
        accept=".xlsx,.xls,.csv"
        clearable
        leftSection={<FileXlsIcon size={18} />}
        onChange={handleFile}
        classNames={{ label: classes.label }}
      />
      <Text className={classes.hint}>{t("excel.hint")}</Text>
      {readCount != null && <Text className={classes.hint}>{t("excel.read", { count: readCount })}</Text>}
      <Box>
        <Button variant="subtle" color="gray" size="sm" leftSection={<DownloadSimpleIcon size={16} />} onClick={handleTemplate}>
          {t("excel.template")}
        </Button>
      </Box>
    </Stack>
  )
}

function BarcodeSource({ text, onTextChange }: Readonly<{ text: string; onTextChange: (text: string) => void }>) {
  const { t } = useTranslation("bulkOrder")
  const [value, setValue] = React.useState("")
  const [last, setLast] = React.useState("")

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return
    e.preventDefault()
    const code = value.trim()
    if (!code) return
    onTextChange(appendCode(text, code))
    setLast(code)
    setValue("")
  }

  return (
    <Stack gap="xs">
      <TextInput
        label={t("barcode.label")}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        leftSection={<BarcodeIcon size={18} />}
        autoFocus
        autoComplete="off"
        classNames={{ label: classes.label, input: classes.textarea }}
      />
      <Text className={classes.hint}>{t("barcode.hint")}</Text>
      {last && (
        <Text className={classes.hint}>
          {t("barcode.last")}{" "}
          <Text span className={classes.mono} translate="no">
            {last}
          </Text>
        </Text>
      )}
    </Stack>
  )
}
